import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Percent, WarningCircle, EyeSlash, Info } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * Iter-41: Admin-only margin dashboard.
 *
 * The one place in the whole product where both sides of the trade are
 * visible — buyer sell price minus vendor cost per state (and per state ×
 * accident-type on the marketplace). Gated to `org_type=internal` at the
 * backend; the header carries a persistent reminder that this data is
 * NEVER exposed to vendors or buyers.
 *
 * Rows are sorted low-margin first so any pricing that's leaking money
 * pulses up top. Threshold input persists to localStorage so an admin's
 * definition of "too thin" sticks between sessions.
 */
export default function AdminMargin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [threshold, setThreshold] = useState(() => {
    const raw = Number(localStorage.getItem("margin:threshold"));
    return Number.isFinite(raw) && raw > 0 ? raw : 50;
  });
  const [showMarketplace, setShowMarketplace] = useState(false);

  useEffect(() => {
    localStorage.setItem("margin:threshold", String(threshold));
  }, [threshold]);

  const load = () => {
    setLoading(true);
    api.get(`/admin/margin/matrix?threshold_dollars=${threshold}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load margin"))
      .finally(() => setLoading(false));
  };

  const [byVendor, setByVendor] = useState(null);
  const [byVendorLoading, setByVendorLoading] = useState(true);

  const loadByVendor = () => {
    setByVendorLoading(true);
    api.get(`/admin/margin/by-vendor?threshold_dollars=${threshold}`)
      .then((r) => setByVendor(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load per-affiliate margin"))
      .finally(() => setByVendorLoading(false));
  };

  // Refetch whenever threshold changes — backend does the flagging.
  useEffect(() => { load(); loadByVendor(); }, [threshold]);

  const totals = data?.totals || {};
  const byState = data?.by_state || [];
  const marketplace = data?.marketplace || [];

  // Filter marketplace to just flagged rows by default (there can be lots of
  // rows once every state × 5 accident types are enumerated).
  const marketplaceRows = useMemo(() => {
    if (showMarketplace) return marketplace;
    return marketplace.filter((r) => r.is_low_margin);
  }, [marketplace, showMarketplace]);

  const flagged = byState.filter((r) => r.is_low_margin);

  return (
    <div className="min-h-screen bg-white text-black" data-testid="admin-margin-page">
      {/* Hero */}
      <div className="border-b border-black">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-8">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="label-mono text-red-700 inline-flex items-center gap-1.5">
                <EyeSlash size={12} weight="bold" /> Internal only — never shared with any affiliate or buyer
              </div>
              <h1 className="font-chivo font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter uppercase mt-1">
                Margin Dashboard
              </h1>
              <div className="text-sm text-zinc-500 mt-1 max-w-2xl">
                Platform take = <b>avg buyer sell price − avg affiliate cost</b>, per
                state (and per state × accident type on the marketplace).
                Flagged states pulse red for one-glance triage.
              </div>
            </div>
            <div className="ml-auto flex items-center gap-3 flex-wrap">
              <label className="inline-flex items-center gap-2 text-xs uppercase tracking-widest">
                Flag margin under
                <input
                  type="number"
                  min="0"
                  step="5"
                  value={threshold}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    if (Number.isFinite(v) && v >= 0) setThreshold(v);
                  }}
                  data-testid="margin-threshold-input"
                  className="w-20 border border-black px-2 py-1 font-mono text-sm text-right"
                />
                <span className="font-mono">$</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Big-number bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-b border-black">
        <div className="p-6 border-r border-black" data-testid="margin-tot-states">
          <div className="label-mono">States covered</div>
          <div className="font-chivo font-black text-4xl mt-1">
            {loading ? "…" : (totals.states_covered ?? 0)}
          </div>
          <div className="text-[11px] text-zinc-500 font-mono mt-1">
            {totals.states_with_margin ?? 0} priced both sides
          </div>
        </div>
        <div className={
          "p-6 border-r border-black " +
          (totals.low_margin_states > 0 ? "bg-red-50" : "")
        } data-testid="margin-tot-lowmargin">
          <div className={"label-mono " + (totals.low_margin_states > 0 ? "text-red-700" : "")}>
            States below ${threshold}
          </div>
          <div className={
            "font-chivo font-black text-5xl mt-1 " +
            (totals.low_margin_states > 0 ? "text-red-700 animate-pulse" : "")
          }>
            {loading ? "…" : (totals.low_margin_states ?? 0)}
          </div>
          <div className="text-[11px] text-zinc-500 font-mono mt-1">
            flagged — pricing needs a nudge
          </div>
        </div>
        <div className="p-6 border-r border-black" data-testid="margin-tot-weighted">
          <div className="label-mono">Weighted avg margin (30d)</div>
          <div className="font-chivo font-black text-4xl mt-1">
            {loading ? "…" : totals.weighted_avg_margin_30d != null
              ? `$${totals.weighted_avg_margin_30d.toFixed(2)}` : "—"}
          </div>
          <div className="text-[11px] text-zinc-500 font-mono mt-1">
            weighted by lead volume per state
          </div>
        </div>
        <div className="p-6 bg-zinc-50" data-testid="margin-tot-volume">
          <div className="label-mono">Volume (30d)</div>
          <div className="font-chivo font-black text-4xl mt-1">
            {loading ? "…" : (totals.volume_30d ?? 0)}
          </div>
          <div className="text-[11px] text-zinc-500 font-mono mt-1">
            non-test leads counted
          </div>
        </div>
      </div>

      {/* Flagged bar */}
      {flagged.length > 0 && (
        <div
          className="border-b border-black bg-red-600 text-white px-4 md:px-8 py-3 flex items-center gap-3 flex-wrap"
          data-testid="margin-flagged-banner"
        >
          <WarningCircle size={16} weight="fill" className="animate-pulse flex-shrink-0" />
          <div className="font-chivo font-black uppercase tracking-tighter">
            {flagged.length} state{flagged.length === 1 ? "" : "s"} bleeding margin:
          </div>
          <div className="font-mono text-xs uppercase tracking-widest break-words min-w-0">
            {flagged.slice(0, 12).map((r) => `${r.state} ($${r.margin?.toFixed(0)})`).join(" · ")}
            {flagged.length > 12 && ` · +${flagged.length - 12} more`}
          </div>
        </div>
      )}

      {/* Per-state margin table */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-8">
        <div className="brut-card mb-8">
          <div className="border-b border-black px-5 py-3 flex items-center gap-2 flex-wrap">
            <Percent size={14} weight="bold" />
            <div className="font-chivo font-black uppercase tracking-tighter">
              Direct routing — buyer sell − affiliate cost
            </div>
            <div className="label-mono ml-2">
              Avg across every buyer / affiliate active for that state
            </div>
          </div>
          <div className="p-5 overflow-x-auto">
            {loading && <div className="label-mono text-center py-4">Loading…</div>}
            {!loading && !byState.length && (
              <div className="label-mono text-center py-4 text-zinc-500">
                No priced states yet. Set <code>state_fees</code> on a buyer destination and{" "}
                <code>state_costs</code> on an affiliate to start seeing margin.
              </div>
            )}
            {!loading && byState.length > 0 && (
              <table className="w-full text-xs" data-testid="margin-by-state-table">
                <thead>
                  <tr className="border-b-2 border-black bg-zinc-100 text-[10px] uppercase tracking-widest">
                    <th className="text-left py-2 px-3">State</th>
                    <th className="text-right py-2 px-3">Affiliates</th>
                    <th className="text-right py-2 px-3">Avg cost</th>
                    <th className="text-right py-2 px-3 border-l border-black">Buyers</th>
                    <th className="text-right py-2 px-3">Avg sell</th>
                    <th className="text-right py-2 px-3 border-l-2 border-black bg-amber-50">Margin</th>
                    <th className="text-right py-2 px-3 bg-amber-50">Margin %</th>
                    <th className="text-right py-2 px-3 border-l border-black">30d vol</th>
                  </tr>
                </thead>
                <tbody>
                  {byState.map((r) => (
                    <tr
                      key={r.state}
                      data-testid={`margin-row-${r.state}`}
                      data-flagged={r.is_low_margin ? "1" : "0"}
                      className={
                        "border-b border-zinc-200 " +
                        (r.is_low_margin ? "bg-red-50/70" : "")
                      }
                    >
                      <td className="py-1.5 px-3 font-mono font-bold">
                        <span className="inline-flex items-center gap-1.5">
                          {r.is_low_margin && (
                            <span className="relative inline-flex h-2 w-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600" />
                            </span>
                          )}
                          {r.state}
                        </span>
                      </td>
                      <td className="py-1.5 px-3 text-right font-mono text-zinc-600">
                        {r.vendor_samples || "—"}
                      </td>
                      <td className="py-1.5 px-3 text-right font-mono">
                        {r.avg_vendor_cost != null ? `$${r.avg_vendor_cost.toFixed(2)}` : "—"}
                      </td>
                      <td className="py-1.5 px-3 text-right font-mono text-zinc-600 border-l border-black">
                        {r.buyer_samples || "—"}
                      </td>
                      <td className="py-1.5 px-3 text-right font-mono">
                        {r.avg_buyer_sell != null ? `$${r.avg_buyer_sell.toFixed(2)}` : "—"}
                      </td>
                      <td className={
                        "py-1.5 px-3 text-right border-l-2 border-black bg-amber-50/70 " +
                        (r.is_low_margin
                          ? "font-chivo font-black text-2xl text-red-700"
                          : "font-mono font-bold")
                      }>
                        {r.margin != null ? `$${r.margin.toFixed(2)}` : "—"}
                      </td>
                      <td className={
                        "py-1.5 px-3 text-right font-mono bg-amber-50/70 " +
                        (r.is_low_margin ? "text-red-700 font-bold" : "text-zinc-600")
                      }>
                        {r.margin_pct != null ? `${r.margin_pct.toFixed(1)}%` : "—"}
                      </td>
                      <td className="py-1.5 px-3 text-right font-mono text-zinc-500 border-l border-black">
                        {r.volume_30d || 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Marketplace matrix */}
        <div className="brut-card">
          <div className="border-b border-black px-5 py-3 flex items-center gap-2 flex-wrap">
            <Percent size={14} weight="bold" />
            <div className="font-chivo font-black uppercase tracking-tighter">
              Marketplace — orphan lead sell − affiliate cost
            </div>
            <div className="label-mono ml-2 inline-flex items-center gap-1">
              <Info size={11} /> State × accident type
            </div>
            <div className="ml-auto">
              <label className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest cursor-pointer">
                <input
                  type="checkbox"
                  checked={showMarketplace}
                  onChange={(e) => setShowMarketplace(e.target.checked)}
                  data-testid="margin-show-all-marketplace"
                  className="accent-black"
                />
                Show all rows ({marketplace.length})
              </label>
            </div>
          </div>
          <div className="p-5 overflow-x-auto">
            {loading && <div className="label-mono text-center py-4">Loading…</div>}
            {!loading && !marketplaceRows.length && (
              <div className="label-mono text-center py-4 text-zinc-500">
                No marketplace rows flagged below ${threshold}. Toggle &ldquo;Show all rows&rdquo; to see the full matrix.
              </div>
            )}
            {!loading && marketplaceRows.length > 0 && (
              <table className="w-full text-xs" data-testid="margin-marketplace-table">
                <thead>
                  <tr className="border-b-2 border-black bg-zinc-100 text-[10px] uppercase tracking-widest">
                    <th className="text-left py-2 px-3">State</th>
                    <th className="text-left py-2 px-3">Accident type</th>
                    <th className="text-right py-2 px-3">Marketplace sell</th>
                    <th className="text-right py-2 px-3">Avg affiliate cost</th>
                    <th className="text-right py-2 px-3 border-l-2 border-black bg-amber-50">Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {marketplaceRows.map((r) => (
                    <tr
                      key={`${r.state}-${r.accident_type}`}
                      data-testid={`margin-mp-row-${r.state}-${r.accident_type}`}
                      className={
                        "border-b border-zinc-200 " +
                        (r.is_low_margin ? "bg-red-50/70" : "")
                      }
                    >
                      <td className="py-1.5 px-3 font-mono font-bold">
                        <span className="inline-flex items-center gap-1.5">
                          {r.is_low_margin && (
                            <span className="relative inline-flex h-2 w-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600" />
                            </span>
                          )}
                          {r.state}
                        </span>
                      </td>
                      <td className="py-1.5 px-3 font-mono text-zinc-700">
                        {r.accident_type}
                      </td>
                      <td className="py-1.5 px-3 text-right font-mono">
                        ${r.marketplace_sell?.toFixed(2)}
                      </td>
                      <td className="py-1.5 px-3 text-right font-mono">
                        {r.avg_vendor_cost != null ? `$${r.avg_vendor_cost.toFixed(2)}` : "—"}
                      </td>
                      <td className={
                        "py-1.5 px-3 text-right border-l-2 border-black bg-amber-50/70 " +
                        (r.is_low_margin
                          ? "font-chivo font-black text-2xl text-red-700"
                          : "font-mono font-bold")
                      }>
                        {r.margin != null ? `$${r.margin.toFixed(2)}` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        {/* Per-vendor margin table */}
        <div className="brut-card mt-8">
          <div className="border-b border-black px-5 py-3 flex items-center gap-2 flex-wrap">
            <Percent size={14} weight="bold" />
            <div className="font-chivo font-black uppercase tracking-tighter">
              Per-affiliate margin — one row per (affiliate × state)
            </div>
            <div className="label-mono ml-2">
              {byVendor?.totals?.vendor_agreements ?? "…"} agreements
              {byVendor?.totals?.low_margin_agreements > 0 && (
                <span className="text-red-700 ml-2 font-bold">
                  · {byVendor.totals.low_margin_agreements} thin-margin
                </span>
              )}
            </div>
          </div>
          <div className="p-5 overflow-x-auto">
            <div className="text-[11px] text-zinc-500 mb-3 leading-relaxed max-w-3xl">
              Buy prices vary by seller agreement, so this table shows every affiliate&apos;s
              per-state cost against the buyer sell price (avg across active buyer
              destinations, or the ${data?.sell_floor?.toFixed(0) ?? "350"} floor when no
              buyer covers that state). Green rows are lucrative; red rows are eating
              margin — consider renegotiating.
            </div>
            {byVendorLoading && <div className="label-mono text-center py-4">Loading…</div>}
            {!byVendorLoading && !byVendor?.rows?.length && (
              <div className="label-mono text-center py-4 text-zinc-500">
                No per-affiliate agreements yet. Set <code>state_costs</code> on any
                affiliate to start seeing per-affiliate margin.
              </div>
            )}
            {!byVendorLoading && byVendor?.rows?.length > 0 && (
              <table className="w-full text-xs" data-testid="margin-by-vendor-table">
                <thead>
                  <tr className="border-b-2 border-black bg-zinc-100 text-[10px] uppercase tracking-widest">
                    <th className="text-left py-2 px-3">Affiliate</th>
                    <th className="text-left py-2 px-3">State</th>
                    <th className="text-right py-2 px-3">Affiliate cost</th>
                    <th className="text-right py-2 px-3 border-l border-black">Sell price</th>
                    <th className="text-right py-2 px-3">Source</th>
                    <th className="text-right py-2 px-3 border-l-2 border-black bg-amber-50">Margin</th>
                    <th className="text-right py-2 px-3 bg-amber-50">Margin %</th>
                  </tr>
                </thead>
                <tbody>
                  {byVendor.rows.map((r) => (
                    <tr
                      key={`${r.vendor_id}-${r.state}`}
                      data-testid={`margin-vendor-row-${r.vendor_id}-${r.state}`}
                      className={
                        "border-b border-zinc-200 " +
                        (r.is_low_margin ? "bg-red-50/70" : "")
                      }
                    >
                      <td className="py-1.5 px-3 font-mono">
                        <span className="inline-flex items-center gap-1.5">
                          {r.is_low_margin && (
                            <span className="relative inline-flex h-2 w-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600" />
                            </span>
                          )}
                          {r.vendor_name}
                        </span>
                      </td>
                      <td className="py-1.5 px-3 font-mono font-bold">{r.state}</td>
                      <td className="py-1.5 px-3 text-right font-mono">
                        ${r.vendor_cost.toFixed(2)}
                      </td>
                      <td className="py-1.5 px-3 text-right font-mono border-l border-black">
                        ${r.sell_price.toFixed(2)}
                      </td>
                      <td className="py-1.5 px-3 text-right font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                        {r.sell_source === "floor" ? "floor" : "buyer avg"}
                      </td>
                      <td className={
                        "py-1.5 px-3 text-right border-l-2 border-black bg-amber-50/70 " +
                        (r.is_low_margin
                          ? "font-chivo font-black text-2xl text-red-700"
                          : "font-mono font-bold text-emerald-700")
                      }>
                        ${r.margin.toFixed(2)}
                      </td>
                      <td className={
                        "py-1.5 px-3 text-right font-mono bg-amber-50/70 " +
                        (r.is_low_margin ? "text-red-700 font-bold" : "text-zinc-600")
                      }>
                        {r.margin_pct != null ? `${r.margin_pct.toFixed(1)}%` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
